import { HeaderPage } from "@/components/HeaderPage";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, Plus, Trash2 } from "lucide-react";
import { Card, CardContent as CardContentUI } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Eye } from "lucide-react";
import { CardContent } from "@/components/CardContent";
import { Table } from "@/components/Table";
import { Loading } from "@/components/Loading";
import { MdOutlineEdit } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { useGetServiceOrders } from "@/hooks/useServiceOrders";
import { useGetSectors } from "@/hooks/useSectors";
import { useDebounce } from "@/hooks/useDebounce";
import { useState } from "react";

export const OrdensServico = () => {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  const [sectorFilter, setSectorFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);

  const debouncedSearch = useDebounce(searchInput, 500);

  const { data: serviceOrdersData, isLoading } = useGetServiceOrders({
    search: debouncedSearch,
    sectorId: sectorFilter,
    status: statusFilter,
    page: currentPage,
    limit: limit,
  });

  const { data: sectorsData } = useGetSectors();

  // Debug: verificar estrutura dos dados
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSectorChange = (value: string) => {
    setSectorFilter(value);
    setCurrentPage(1);
  };

  const handleStatusChange = (value: string) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
    setCurrentPage(1);
  };

  const priorityLabels: Record<string, { label: string; color: string }> = {
    LOW: { label: "Baixa", color: "bg-blue-500" },
    MEDIUM: { label: "Média", color: "bg-yellow-500" },
    HIGH: { label: "Alta", color: "bg-orange-500" },
    URGENT: { label: "Crítica", color: "bg-red-500" },
  };

  const statusLabels: Record<string, { label: string; color: string }> = {
    PENDING: { label: "Pendente", color: "bg-gray-500" },
    IN_PROGRESS: { label: "Em Andamento", color: "bg-blue-600" },
    AWAITING_VALIDATION: { label: "Aguardando Validação", color: "bg-amber-500" },
    SUSPENDED: { label: "Suspensa", color: "bg-yellow-600" },
    COMPLETED: { label: "Concluída", color: "bg-green-500" },
    CANCELLED: { label: "Cancelada", color: "bg-red-500" },
  };
  return (
    <>
      <HeaderPage
        title="Ordens de serviço"
        description="Acompanhe e gerencie as ordens de serviço dos setores"
      />

      {/* Header Actions */}
      <Card className="mb-6 mt-6 bg-white">
        <CardContentUI className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar ordens de serviço..."
                className="pl-10 h-10 text-sm"
                value={searchInput}
                onChange={handleSearchChange}
              />
            </div>
            <div className="flex gap-3">
              <Select value={sectorFilter} onValueChange={handleSectorChange}>
                <SelectTrigger className="w-[180px] h-10 text-sm">
                  <SelectValue placeholder="Setor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {sectorsData?.data?.map((sector: any) => (
                    <SelectItem key={sector.id} value={sector.id}>
                      {sector.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={handleStatusChange}>
                <SelectTrigger className="w-[180px] h-10 text-sm">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="PENDING">Aguardando</SelectItem>
                  <SelectItem value="IN_PROGRESS">Em Andamento</SelectItem>
                  <SelectItem value="COMPLETED">Concluída</SelectItem>
                  <SelectItem value="CANCELLED">Cancelada</SelectItem>
                </SelectContent>
              </Select>
              <Button
                className="h-10 text-sm px-4 text-white cursor-pointer bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--primary))]"
                onClick={() => navigate("/service-orders/create")}
              >
                <Plus className="h-4 w-4 mr-2" />
                Nova O.S
              </Button>
            </div>
          </div>
        </CardContentUI>
      </Card>
      <CardContent
        title="Lista de Ordens de Serviço"
        content={
          isLoading ? (
            <Loading label="Carregando ordens de serviço..." />
          ) : (
            <Table
              columns={[
                { key: "id", label: "Id" },
                { key: "title", label: "Título" },
                {
                  key: "priority",
                  label: "Prioridade",
                  render: (order: any) => {
                    const priority = priorityLabels[order.priority];
                    return (
                      <Badge
                        variant="default"
                        className={priority?.color || "bg-gray-500"}
                      >
                        {priority?.label || order.priority}
                      </Badge>
                    );
                  },
                },
                {
                  key: "sector",
                  label: "Setor",
                  render: (order: any) => order.sector?.name || "N/A",
                },
                {
                  key: "assignedTo",
                  label: "Atribuído a",
                  render: (order: any) => {
                    if (
                      order.assignedTo?.firstName ||
                      order.assignedTo?.lastName
                    ) {
                      return `${order.assignedTo.firstName || ""} ${order.assignedTo.lastName || ""}`.trim();
                    }
                    return "N/A";
                  },
                },
                {
                  key: "status",
                  label: "Status",
                  render: (order: any) => {
                    const status = statusLabels[order.status];
                    return (
                      <Badge
                        variant="default"
                        className={status?.color || "bg-gray-500"}
                      >
                        {status?.label || order.status}
                      </Badge>
                    );
                  },
                },
                {
                  key: "estimatedStartDate",
                  label: "Início Estimado",
                  render: (order: any) =>
                    order.estimatedStartDate
                      ? new Date(order.estimatedStartDate).toLocaleDateString(
                          "pt-BR",
                        )
                      : "N/A",
                },
                {
                  key: "actions",
                  label: "Ações",
                  render: (order: any) => {
                    return (
                      <div className="flex items-center">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 cursor-pointer hover:bg-gray-100 transition-colors"
                          title="Visualizar"
                          onClick={() =>
                            navigate(`/service-orders/view/${order.id}`)
                          }
                        >
                          <Eye className="h-5 w-5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 cursor-pointer hover:bg-gray-100 transition-colors"
                          title="Editar"
                          onClick={() =>
                            navigate(`/service-orders/edit/${order.id}`)
                          }
                        >
                          <MdOutlineEdit className="h-5 w-5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 cursor-pointer hover:bg-red-50 transition-colors"
                          title="Deletar"
                          onClick={() => {}}
                        >
                          <Trash2 className="h-5 w-5" />
                        </Button>
                      </div>
                    );
                  },
                },
              ]}
              data={serviceOrdersData?.data || []}
              pagination={
                serviceOrdersData?.pagination || {
                  total: serviceOrdersData?.data?.length || 0,
                  page: currentPage,
                  limit: limit,
                  totalPages: Math.ceil(
                    (serviceOrdersData?.data?.length || 0) / limit,
                  ),
                }
              }
              onPageChange={handlePageChange}
            />
          )
        }
      />
    </>
  );
};
